import React, { useEffect, useState } from 'react';
import { ClockIcon } from 'lucide-react';
import { DAILY_CAMPAIGN } from '../config/site';
interface WarsawNow {
  dateLabel: string;
  secondsUntilCutoff: number;
}
function cutoffSeconds(): number {
  const [h, m, s] = DAILY_CAMPAIGN.cutoff.split(':').map(part => Number(part));
  return h * 3600 + m * 60 + s;
}

/** Aktualny czas w strefie Europe/Warsaw, niezależnie od strefy przeglądarki. */
function readWarsawNow(): WarsawNow {
  const formatter = new Intl.DateTimeFormat('pl-PL', {
    timeZone: DAILY_CAMPAIGN.timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });
  const parts = formatter.formatToParts(new Date());
  const get = (type: string) => Number(parts.find(p => p.type === type)?.value ?? '0');
  const hour = get('hour') % 24;
  const elapsed = hour * 3600 + get('minute') * 60 + get('second');
  const day = String(get('day')).padStart(2, '0');
  const month = String(get('month')).padStart(2, '0');
  return {
    dateLabel: `${day}.${month}.${get('year')}`,
    secondsUntilCutoff: Math.max(0, cutoffSeconds() - elapsed)
  };
}
function TimeUnit({
  value,
  label
}: {
  value: number;
  label: string;
}) {
  return <div className="flex min-w-[52px] flex-col items-center" data-id="element-82" data-mp="3jfebqdjtpkvz">
      <span className="font-display text-3xl font-bold leading-none text-white tabular-nums sm:text-4xl" data-id="element-83" data-mp="3jfxutxl7mfbl">
        {String(value).padStart(2, '0')}
      </span>
      <span className="mt-1.5 text-[10px] font-semibold tracking-[0.14em] text-white/55" data-id="element-84" data-mp="f1ppmznrla2m">
        {label}
      </span>
    </div>;
}
export function DailyCountdown() {
  const [now, setNow] = useState<WarsawNow>(readWarsawNow);
  useEffect(() => {
    const timer = window.setInterval(() => setNow(readWarsawNow()), 1000);
    return () => window.clearInterval(timer);
  }, []);
  if (!DAILY_CAMPAIGN.enabled || now.secondsUntilCutoff <= 0) return null;
  const hours = Math.floor(now.secondsUntilCutoff / 3600);
  const minutes = Math.floor(now.secondsUntilCutoff % 3600 / 60);
  const seconds = now.secondsUntilCutoff % 60;
  return <aside aria-label="Czas pozostały do końca dzisiejszej oferty" className="mt-4 rounded-2xl border border-chem/60 bg-navy px-5 py-5 text-white shadow-[0_18px_34px_-22px_rgba(11,29,58,0.9)]" data-id="element-85" data-mp="13k9iaqjv90q1">
      <div className="flex flex-col items-center gap-4 text-center sm:flex-row sm:justify-between sm:gap-6 sm:text-left" data-id="element-86" data-mp="tqj186q2jskd">
        <div className="min-w-0" data-id="element-87" data-mp="kvbbas3gl8du">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-chem/60 bg-chem/20 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-white" data-id="element-88" data-mp="kv2zz4mzib4r">
            <ClockIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-89" data-mp="ayq543fpdr8f" />
            Oferta na dzisiaj
          </span>
          {DAILY_CAMPAIGN.dateLabel && <p className="mt-2 text-xs font-semibold tracking-[0.16em] text-white/60" data-id="element-90" data-mp="60eye1kpfwuf">
              {now.dateLabel}
            </p>}
          <p className="mt-2 text-sm font-semibold leading-snug text-white" data-id="element-91" data-mp="2djann2vrknho">
            Dzisiejsza oferta kończy się o północy.
          </p>
          <p className="mt-1 text-sm leading-snug text-white/70" data-id="element-92" data-mp="2ho0fmgnaxhxb">
            Zabezpiecz dostęp do Wizualnej Chemii™ zanim zakończy się bieżąca kampania.
          </p>
        </div>

        <div className="flex shrink-0 items-start gap-2" role="timer" aria-live="off" data-id="element-93" data-mp="2rjbzfddkhfbx">
          <TimeUnit value={hours} label="GODZ." data-id="element-94" data-mp="1txxteh62j66m" />
          <span className="font-display text-2xl font-bold text-chem" aria-hidden="true" data-id="element-95" data-mp="1yv6dwoeovmb1">
            :
          </span>
          <TimeUnit value={minutes} label="MIN." data-id="element-96" data-mp="23u96h9dhg024" />
          <span className="font-display text-2xl font-bold text-chem" aria-hidden="true" data-id="element-97" data-mp="23sg0zxelvlvp">
            :
          </span>
          <TimeUnit value={seconds} label="SEK." data-id="element-98" data-mp="1b5v82sihf1q1" />
        </div>
      </div>
    </aside>;
}